import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '@/apiRequest';

/**
 * Hook to fetch order data for editing
 */
export const useGetOrderFormEdit = (orderId) => {
  return useQuery({
    queryKey: ['orderFormEdit', orderId],
    queryFn: async () => {
      const { data } = await API.get(`/orders/${orderId}`);
      if (!data) throw new Error('Order data not found');
      return data;
    },
    enabled: !!orderId,
    staleTime: 0,
  });
};

/**
 * Hook to fetch order template for creating a new order
 */
export const useGetOrderFormNew = (newOrderType, previousOrderId, previousPropertyId, reportHash) => {
  return useQuery({
    queryKey: ['orderFormNew', newOrderType, previousOrderId, previousPropertyId, reportHash],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('type', newOrderType);
      if (previousOrderId) params.append('previous_id', previousOrderId);
      if (previousPropertyId) params.append('property_id', previousPropertyId);
      if (reportHash) params.append('report_hash', reportHash);
      
      const { data } = await API.get('/orders/new', { params });
      return data;
    },
    enabled: !!newOrderType,
    staleTime: 5000,
  });
};


/**
 * Combined mutations for Order Form operations
 */
export const useOrderFormMutations = (orderId = null) => {
  const queryClient = useQueryClient();

  const saveOrderMutation = useMutation({
    mutationFn: async (formData) => {
      const { data } = await API.put(`/orders/${orderId}`, JSON.stringify(formData));
      return data;
    },
    onSuccess: () => {
      window.sessionStorage.removeItem('cachedTags');
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      if (orderId) {
        queryClient.invalidateQueries({ queryKey: ['orderFormEdit', orderId] });
        queryClient.invalidateQueries({ queryKey: ['order', orderId] });
        queryClient.invalidateQueries({ queryKey: ['inspection', orderId] });
      }
    },
  });

  const createOrderMutation = useMutation({
    mutationFn: async (formData) => {
      const { data } = await API.post('/orders', JSON.stringify(formData));
      return data;
    },
    onSuccess: () => {
      window.sessionStorage.removeItem('cachedTags');
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });

  const packageConsumeMutation = useMutation({
    mutationFn: async (formData) => {
      const { data } = await API.post('/financial/packageconsume', JSON.stringify(formData));
      return data;
    },
  });

  const purchasePackageMutation = useMutation({
    mutationFn: async ({ packageId, clientId }) => {
      const { data } = await API.post(`/packages/${packageId}/clients`, { 
        client_id: clientId 
      });
      return data;
    },
  });

  const calculateOrderPriceMutation = useMutation({
    mutationFn: async (formData) => {
      const { data } = await API.post('/orders/calculatePrice', JSON.stringify(formData));
      return data;
    },
  });

  return {
    packageConsume: packageConsumeMutation,
    saveOrder: saveOrderMutation,
    createOrder: createOrderMutation,
    purchasePackage: purchasePackageMutation,
    calculateOrderPrice: calculateOrderPriceMutation,
  };
};
